import { first_value } from '@/services/onboarding_data_helpers';

const AGREEMENT_KEYWORDS = ['agreement', 'contract', 'mou', 'nda', 'letter', 'offer', 'policy', 'esign', 'e-sign', 'signature'];
const AGREEMENT_STATUS_META_RULES = [
  {
    label: 'Signed',
    words: ['completed', 'signed', 'verified'],
    pill_class: 'bg-success-subtle text-success-emphasis',
    icon_class: 'bg-success-subtle text-success-emphasis',
    icon: 'bi-check2-circle'
  },
  {
    label: 'In progress',
    words: ['opened', 'viewed', 'in_progress', 'in progress'],
    pill_class: 'bg-primary-subtle text-primary',
    icon_class: 'bg-primary-subtle text-primary',
    icon: 'bi-eye'
  },
  {
    label: 'Declined',
    words: ['declined', 'rejected', 'failed'],
    pill_class: 'bg-danger-subtle text-danger-emphasis',
    icon_class: 'bg-danger-subtle text-danger-emphasis',
    icon: 'bi-x-circle'
  }
];
const DEFAULT_AGREEMENT_STATUS_META = {
  label: 'Sent',
  pill_class: 'bg-warning-subtle text-warning-emphasis',
  icon_class: 'bg-warning-subtle text-warning-emphasis',
  icon: 'bi-send'
};

function normalize_text_value(value = '') {
  return String(value || '').trim();
}

function normalize_status_key(value = '') {
  return normalize_text_value(value).toLowerCase();
}

function build_name_from_parts(firstName = '', lastName = '') {
  return [firstName, lastName].map((part) => normalize_text_value(part)).filter(Boolean).join(' ');
}

function get_document_signer_collections(document = {}) {
  return [
    document?.signers,
    document?.recipients,
    document?.recipient_details,
    document?.assigned_signers,
    document?.completed_signers,
    document?.participants,
    document?.tracking?.signers,
    document?.submission?.signers
  ].filter(Array.isArray);
}

function flatten_document_signers(document = {}) {
  return get_document_signer_collections(document).flat().filter(Boolean);
}

function get_signer_name(signer = {}) {
  const user = signer?.user || signer?.assigned_to || {};
  const composedName = build_name_from_parts(
    first_value([signer?.first_name, user?.first_name], ''),
    first_value([signer?.last_name, user?.last_name], '')
  );

  return first_value([
    signer?.full_name,
    signer?.name,
    signer?.recipient_name,
    signer?.signer_name,
    signer?.completed_by_name,
    user?.full_name,
    user?.name,
    composedName
  ], '');
}

function signer_is_external(signer = {}) {
  if ([signer?.is_external, signer?.external, signer?.external_signer].some((flag) => flag === true)) {
    return true;
  }

  return [
    signer?.source_type,
    signer?.signer_type,
    signer?.type,
    signer?.user_type,
    signer?.role,
    signer?.role_name
  ]
    .map((value) => normalize_status_key(value))
    .filter(Boolean)
    .some((value) => value.includes('external'));
}

function signer_is_completed(signer = {}) {
  const normalizedStatus = normalize_status_key(first_value([
    signer?.status,
    signer?.display_status,
    signer?.overall_status
  ], ''));

  return Boolean(
    signer?.completed_at
    || signer?.signed_at
    || signer?.finished_at
    || ['completed', 'signed', 'finished'].some((word) => normalizedStatus.includes(word))
  );
}

function unique_values(values = []) {
  return [...new Set(values.filter(Boolean))];
}

function get_internal_signer_names(document = {}) {
  const internalSigners = flatten_document_signers(document)
    .filter((signer) => !signer_is_external(signer));
  const completedInternalSigners = internalSigners.filter((signer) => signer_is_completed(signer));
  const preferredSigners = completedInternalSigners.length ? completedInternalSigners : internalSigners;

  return unique_values(preferredSigners.map((signer) => get_signer_name(signer)));
}

function get_agreement_signed_by(document = {}, fallbackName = '') {
  const signerNames = get_internal_signer_names(document);
  if (signerNames.length) {
    return signerNames.join(', ');
  }

  const directName = first_value([
    document?.signed_by_name,
    document?.completed_by_name,
    document?.internal_signer_name
  ], '');
  if (directName && !normalize_status_key(directName).includes('external')) {
    return directName;
  }

  return fallbackName;
}

function get_agreement_status_meta(document = {}) {
  const normalizedStatus = normalize_status_key(first_value([
    document?.status,
    document?.overall_status,
    document?.display_status
  ], ''));
  const matchedStatusMeta = document?.is_verified
    ? AGREEMENT_STATUS_META_RULES[0]
    : AGREEMENT_STATUS_META_RULES.find(({ words }) => (
      words.some((word) => normalizedStatus.includes(word))
    ));
  const statusMeta = matchedStatusMeta || DEFAULT_AGREEMENT_STATUS_META;
  return statusMeta;
}

function get_agreement_submission_id(document = {}) {
  const parsedSubmissionId = Number(first_value([
    document?.submission_id,
    document?.id,
    document?.submission?.id
  ], 0));

  return Number.isFinite(parsedSubmissionId) && parsedSubmissionId > 0 ? parsedSubmissionId : 0;
}

function document_looks_like_agreement(document = {}) {
  if (document?.docuseal_submission_id || document?.submission_id) {
    return true;
  }

  const searchableText = [
    document?.title,
    document?.file_name,
    document?.question,
    document?.section,
    document?.type
  ]
    .map((value) => normalize_status_key(value))
    .join(' ');

  return AGREEMENT_KEYWORDS.some((word) => searchableText.includes(word));
}

function get_agreement_collection(caseItem = {}) {
  return Array.isArray(caseItem?.agreements) ? caseItem.agreements.filter(Boolean) : [];
}

function get_document_collection(caseItem = {}) {
  return Array.isArray(caseItem?.documents) ? caseItem.documents.filter(Boolean) : [];
}

function get_case_document_candidates(caseItem = {}) {
  const agreements = get_agreement_collection(caseItem);
  return agreements.length ? agreements : get_document_collection(caseItem);
}

function get_preferred_agreement_documents(documents = []) {
  const agreementDocuments = documents.filter((document) => document_looks_like_agreement(document));
  return agreementDocuments.length ? agreementDocuments : documents;
}

function get_case_agreement_documents(caseItem = {}) {
  const documents = get_case_document_candidates(caseItem);
  return get_preferred_agreement_documents(documents);
}

function build_case_agreement_row(document = {}, index = 0, fallbackSignerName = '') {
  const statusMeta = get_agreement_status_meta(document);
  const signedBy = get_agreement_signed_by(document, fallbackSignerName);
  const signedByDetail = statusMeta.label === 'Signed'
    ? (signedBy ? `Signed by ${signedBy}` : 'Signed internally')
    : (signedBy ? `Internal signer: ${signedBy}` : 'Internal signer not available');
  const agreementRow = {
    id: first_value([
      document?.id,
      document?.docuseal_submission_id,
      document?.submission_id,
      document?.file_name,
      document?.question
    ], `agreement-${index}`),
    title: first_value([
      document?.title,
      document?.file_name,
      document?.question,
      document?.section
    ], 'Agreement document'),
    signed_by: signedBy,
    signed_by_detail: signedByDetail,
    submission_id: get_agreement_submission_id(document),
    status: statusMeta.label,
    status_class: statusMeta.pill_class,
    icon_class: statusMeta.icon_class,
    icon: statusMeta.icon
  };
  return agreementRow;
}

export function build_case_agreement_rows(caseItem = {}) {
  const selectedDocuments = get_case_agreement_documents(caseItem);
  const fallbackSignerName = normalize_text_value(caseItem?.name);
  const agreementRows = selectedDocuments.map((document, index) => (
    build_case_agreement_row(document, index, fallbackSignerName)
  ));
  return agreementRows;
}
